import React, { useState, useEffect, useRef, useCallback, useContext } from 'react'
import {
    Container, Button, TextField, Grid, Typography, Card,
    FormControlLabel, Radio, RadioGroup, Pagination, Box, InputAdornment, Divider, Chip, Stack
} from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FeaturedProperties from '../components/featuredProperties'
import { getProperties } from '../backend/api'
import LoadingComponent from '../components/loadingComponent'
import ErrorComponent from '../components/errorScreen'
import DropDownButton from '../components/dropDownButton';
import EmptyPropertys from '../components/emptyPropertys';
import { CurrencyContext } from '../currencyContext'

const sortValues = [
    { label: 'Price',   value: 'price' },
    { label: 'Size',    value: 'area_sqft' },
    { label: 'Newest',  value: '-created_at' },
]

const filterValues = [
    {
        title: 'Category',
        parameter: 'category',
        options: [
            { title: 'Property', value: 'property' },
            { title: 'Land',     value: 'land' },
        ],
    },
    {
        title: 'Property Type',
        parameter: 'property_type',
        options: [
            { title: 'Residential',  value: 'residential' },
            { title: 'Commercial',   value: 'commercial' },
            { title: 'Agricultural', value: 'agricultural' },
            { title: 'Industrial',   value: 'industrial' },
            { title: 'Mixed Use',    value: 'mixed_use' },
        ],
    },
    {
        title: 'Status',
        parameter: 'status',
        options: [
            { title: 'Available',   value: 'available' },
            { title: 'Under Offer', value: 'under_offer' },
            { title: 'Sold',        value: 'sold' },
        ],
    },
    {
        title: 'Parish',
        parameter: 'parish',
        options: [
            { title: 'Saint Andrew',  value: 'saint_andrew' },
            { title: 'Saint David',   value: 'saint_david' },
            { title: 'Saint George',  value: 'saint_george' },
            { title: 'Saint John',    value: 'saint_john' },
            { title: 'Saint Joseph',  value: 'saint_joseph' },
            { title: 'Saint Luke',    value: 'saint_luke' },
            { title: 'Saint Mark',    value: 'saint_mark' },
            { title: 'Saint Patrick', value: 'saint_patrick' },
            { title: 'Saint Paul',    value: 'saint_paul' },
            { title: 'Saint Peter',   value: 'saint_peter' },
        ],
    },
]

const PAGE_SIZE = 12

export default function MarketPage() {
    const currency = useContext(CurrencyContext).value

    const [loading, toggleLoading] = useState(true)
    const [error, toggleError] = useState(false)
    const [properties, setProperties] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)

    const [search, setSearch] = useState('')
    const searchTimer = useRef(null)

    const [sortValue, setSortValue] = useState(sortValues[0].value)
    const [sortAsc, toggleSortMode] = useState(true)
    const [filterProps, setFilterProps] = useState({})
    const [priceMin, setPriceMin] = useState('')
    const [priceMax, setPriceMax] = useState('')
    const [areaMin, setAreaMin] = useState('')
    const [areaMax, setAreaMax] = useState('')

    const [sortOpen, toggleSortOpen] = useState(false)
    const [filterOpen, toggleFilterOpen] = useState(false)

    const totalPages = Math.ceil(totalCount / PAGE_SIZE)

    const buildParams = useCallback(() => {
        const params = { page: currentPage }
        if (search.trim()) params.search = search.trim()
        params.ordering = sortAsc ? sortValue : `-${sortValue}`
        Object.entries(filterProps).forEach(([k, v]) => { if (v) params[k] = v })

        // Price — convert from display currency back to XCD (stored as XCD)
        if (priceMin !== '') params.price_min = Math.round(parseFloat(priceMin) / currency.value)
        if (priceMax !== '') params.price_max = Math.round(parseFloat(priceMax) / currency.value)

        // Area — stored in sqft
        if (areaMin !== '') params.area_min = parseFloat(areaMin)
        if (areaMax !== '') params.area_max = parseFloat(areaMax)

        return params
    }, [currentPage, search, sortValue, sortAsc, filterProps, priceMin, priceMax, areaMin, areaMax, currency.value])

    const getData = useCallback(async () => {
        try {
            toggleLoading(true)
            const data = await getProperties(buildParams())
            setProperties(data?.results || [])
            setTotalCount(data?.count || 0)
            toggleError(false)
        } catch (e) {
            toggleError(true)
        } finally {
            toggleLoading(false)
        }
    }, [buildParams])

    // Fetch when sort / filter / page changes (immediate)
    useEffect(() => { getData() }, [sortValue, sortAsc, filterProps, currentPage, priceMin, priceMax, areaMin, areaMax, getData])

    // Debounce search input (500ms)
    useEffect(() => {
        clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(() => {
            setCurrentPage(1)
            getData()
        }, 500)
        return () => clearTimeout(searchTimer.current)
    }, [search, getData])

    function handleFilterChange(value, parameter) {
        setCurrentPage(1)
        setFilterProps(prev => ({ ...prev, [parameter]: value }))
    }

    function changeSort(value) {
        setCurrentPage(1)
        if (sortValue === value) toggleSortMode(s => !s)
        else { setSortValue(value); toggleSortMode(true) }
    }

    function clearAll() {
        setFilterProps({})
        setPriceMin(''); setPriceMax('')
        setAreaMin(''); setAreaMax('')
        setCurrentPage(1)
    }

    return (
        <Container maxWidth='xl' disableGutters sx={{ py: 5, px: 4 }}>
            <Card sx={{ my: 1, padding: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant='h5' textAlign='center' sx={{ my: 2 }}>Find Your Property</Typography>

                {/* Search */}
                <TextField
                    value={search}
                    onChange={e => { setSearch(e.target.value); setCurrentPage(1) }}
                    fullWidth variant='outlined' type='search'
                    label='Search by title, description or location…'
                    sx={{ mb: 2 }}
                />

                {/* Sort */}
                <DropDownButton sx={{ mb: 1 }} onClick={() => toggleSortOpen(o => !o)} isOpen={sortOpen}>
                    <Typography textAlign='center' color='text.primary' variant='h6'>Sort</Typography>
                </DropDownButton>
                {sortOpen && (
                    <Grid container spacing={2} sx={{ width: '100%', mb: 2, justifyContent: 'center' }}>
                        {sortValues.map(sort => (
                            <Grid item xs='auto' key={sort.value}>
                                <Button
                                    variant={sortValue === sort.value ? 'contained' : 'text'}
                                    onClick={() => changeSort(sort.value)}
                                    sx={{ mx: 1 }}
                                    endIcon={
                                        sortValue === sort.value
                                            ? (sortAsc ? <ArrowUpwardIcon fontSize='small' /> : <ArrowDownwardIcon fontSize='small' />)
                                            : null
                                    }
                                >
                                    {sort.label}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Filter */}
                <DropDownButton sx={{ mb: 1 }} isOpen={filterOpen} onClick={() => toggleFilterOpen(o => !o)}>
                    <Typography color='text.primary' textAlign='center' variant='h6'>Filter</Typography>
                </DropDownButton>
                {filterOpen && (
                    <Box sx={{ width: '100%', mb: 2 }}>
                        {/* Price Range */}
                        <Typography variant='subtitle2' color='green' textAlign='center' sx={{ mb: 1 }}>
                            Price Range ({currency.name})
                        </Typography>
                        <Grid container spacing={2} justifyContent='center' sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth type='number' label='Min Price'
                                    value={priceMin}
                                    onChange={e => { setPriceMin(e.target.value); setCurrentPage(1) }}
                                    InputProps={{ startAdornment: <InputAdornment position='start'><AttachMoneyIcon fontSize='small' /></InputAdornment> }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth type='number' label='Max Price'
                                    value={priceMax}
                                    onChange={e => { setPriceMax(e.target.value); setCurrentPage(1) }}
                                    InputProps={{ startAdornment: <InputAdornment position='start'><AttachMoneyIcon fontSize='small' /></InputAdornment> }}
                                />
                            </Grid>
                        </Grid>

                        {/* Area Range */}
                        <Typography variant='subtitle2' color='green' textAlign='center' sx={{ mb: 1 }}>
                            Area Range (sq ft)
                        </Typography>
                        <Grid container spacing={2} justifyContent='center' sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth type='number' label='Min Area (sq ft)'
                                    value={areaMin}
                                    onChange={e => { setAreaMin(e.target.value); setCurrentPage(1) }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth type='number' label='Max Area (sq ft)'
                                    value={areaMax}
                                    onChange={e => { setAreaMax(e.target.value); setCurrentPage(1) }}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Category / Type / Status / Parish */}
                        <Grid container spacing={2} alignItems='flex-start' direction={{ md: 'row', xs: 'column' }}>
                            {filterValues.map(val => (
                                <Grid sx={styles.filterGrid} item xs='auto' key={val.parameter}>
                                    <Typography textAlign='center' color='green'>{val.title}</Typography>
                                    <RadioGroup
                                        onChange={e => handleFilterChange(e.target.value, val.parameter)}
                                        aria-label={val.title}
                                        row
                                    >
                                        {val.options.map(option => (
                                            <FormControlLabel
                                                key={option.value}
                                                checked={filterProps[val.parameter] === option.value}
                                                value={option.value}
                                                label={option.title}
                                                control={<Radio size='small' />}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                <Button onClick={clearAll} sx={{ mb: 1 }}>Clear All Filters</Button>
            </Card>

            {/* Active filter chips */}
            {(() => {
                const chips = []
                if (search.trim()) chips.push({ key: 'search', label: `"${search.trim()}"`, onDelete: () => setSearch('') })
                if (priceMin)      chips.push({ key: 'priceMin', label: `Min ${priceMin} ${currency.name}`, onDelete: () => setPriceMin('') })
                if (priceMax)      chips.push({ key: 'priceMax', label: `Max ${priceMax} ${currency.name}`, onDelete: () => setPriceMax('') })
                if (areaMin)       chips.push({ key: 'areaMin', label: `Min ${areaMin} sqft`, onDelete: () => setAreaMin('') })
                if (areaMax)       chips.push({ key: 'areaMax', label: `Max ${areaMax} sqft`, onDelete: () => setAreaMax('') })
                filterValues.forEach(fv => {
                    const val = filterProps[fv.parameter]
                    if (val) {
                        const opt = fv.options.find(o => o.value === val)
                        chips.push({ key: fv.parameter, label: `${fv.title}: ${opt?.title || val}`, onDelete: () => setFilterProps(prev => { const n={...prev}; delete n[fv.parameter]; return n }) })
                    }
                })
                if (chips.length === 0) return null
                return (
                    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mt: 1.5, mb: 0.5 }}>
                        {chips.map(c => (
                            <Chip key={c.key} label={c.label} onDelete={c.onDelete} size='small' variant='outlined' color='primary' />
                        ))}
                        {chips.length > 1 && (
                            <Chip label='Clear all' onDelete={clearAll} onClick={clearAll} size='small' color='default' />
                        )}
                    </Stack>
                )
            })()}

            {/* Result count */}
            {!loading && !error && (
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1, ml: 1 }}>
                    {totalCount === 0
                        ? 'No properties found'
                        : `${totalCount} propert${totalCount === 1 ? 'y' : 'ies'} found`}
                </Typography>
            )}

            {/* Results */}
            {loading
                ? <LoadingComponent style={{ maxHeight: '10vh' }} />
                : error
                    ? <ErrorComponent errorText='Could not retrieve properties' buttonText='Try again' onClick={getData} />
                    : properties.length > 0
                        ? <FeaturedProperties properties={properties} />
                        : <EmptyPropertys />
            }

            {/* Pagination */}
            {!loading && !error && totalPages > 1 && (
                <Box display='flex' justifyContent='center' mt={4}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                        color='primary'
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Container>
    )
}

const styles = {
    filterGrid: {
        backgroundColor: 'background.lightSecondary',
        padding: 2,
        borderRadius: 5,
        mx: 1,
        mb: 1,
    }
}
